// Central data for all service pages
// Each service is a full landing page targeting a specific search query.

export const services = {
  termites: {
    slug: "termites",
    code: "S-03",
    title: "Termite & Wood-Destroying Insect Treatment",
    h1: "Termite Treatment in Pittsburgh",
    tagline: "Stop the damage. Save the structure.",
    metaTitle: "Termite Treatment Pittsburgh, PA · Blue Collar World Pest Control",
    metaDescription: "Licensed termite treatment in Pittsburgh. Full inspection, structural treatment, and warranty-backed eradication. Call 484-764-3392 for a free termite inspection.",
    hero:
      "Termites cause $5 billion in U.S. property damage every year. In Pittsburgh's older housing stock — especially south of the river — subterranean termites are relentless. We inspect, treat and protect with warranty-backed methods.",
    signs: [
      "Mud tubes on foundation walls or basement joists",
      "Hollow-sounding wood around door frames or windowsills",
      "Discarded wings near lights or window sills in spring",
      "Small piles of what looks like sawdust (frass)",
      "Buckling paint or bubbling wallpaper on wood surfaces",
      "Sagging floors, sticky doors, cracking crown molding",
    ],
    process: [
      { title: "Free Inspection", body: "Full property walkthrough — foundation, crawlspaces, wood framing, attic. Real diagnosis with photos." },
      { title: "Written Report", body: "You get a written treatment plan with photos, damage assessment, and transparent pricing." },
      { title: "Targeted Treatment", body: "Liquid termiticide barrier + Sentricon-style bait system as needed. EPA-approved formulations only." },
      { title: "Warranty & Monitoring", body: "Annual re-inspections included. Written warranty backing the work." },
    ],
    pests: ["Subterranean termites", "Drywood termites", "Carpenter ants", "Carpenter bees", "Powderpost beetles", "Old house borers"],
    faqs: [
      { q: "How much does termite treatment cost in Pittsburgh?", a: "Depends on home size and infestation level. Free inspection first — you'll get a written quote, no obligation. Most Pittsburgh row homes and Cape Cods fall in the $800–$1,800 range." },
      { q: "How fast does treatment work?", a: "Foraging termites usually die within 3–5 days of contact with modern non-repellent termiticides. Full colony elimination takes 30–90 days." },
      { q: "Is termite treatment safe for my family?", a: "Yes. Modern termiticides are applied to soil around the foundation and are not accessible to pets or kids once dry. We follow EPA labels strictly." },
    ],
  },
  "bed-bugs": {
    slug: "bed-bugs",
    code: "S-06",
    title: "Bed Bug Extermination",
    h1: "Bed Bug Removal in Pittsburgh",
    tagline: "Discreet. Thorough. Guaranteed.",
    metaTitle: "Bed Bug Removal Pittsburgh, PA · Blue Collar World Pest Control",
    metaDescription: "Bed bug extermination in Pittsburgh — heat treatment + chemical protocols. Discreet, effective, guaranteed. Homes, apartments, hotels, rentals. Call 484-764-3392.",
    hero:
      "Bed bugs don't discriminate — cleanest homes get them from luggage, used furniture, or shared laundry. Pittsburgh has one of the highest bed bug complaint rates in PA. We eradicate infestations at any severity, without the headache.",
    signs: [
      "Small itchy welts in a line or cluster on your skin",
      "Rust-colored blood spots on sheets or mattress seams",
      "Tiny black droppings along seams, headboards, or wall trim",
      "Sweet musty smell in the bedroom",
      "Live bugs — flat, apple-seed sized, reddish-brown",
      "Shed skins or eggs in mattress folds",
    ],
    process: [
      { title: "Discreet Inspection", body: "Unmarked vehicles available on request. Full room-by-room infestation mapping." },
      { title: "Prep Instructions", body: "Detailed prep checklist so treatment hits every hiding spot on day one." },
      { title: "Multi-Method Treatment", body: "Heat treatment (135°F kills all life stages), chemical residuals in cracks, mattress encasements as needed." },
      { title: "Follow-Up Guarantee", body: "2-3 follow-up visits included. Written 30-day warranty. We stay until they're gone." },
    ],
    pests: ["Adult bed bugs", "Nymphs", "Eggs", "Bat bugs (similar species)"],
    faqs: [
      { q: "How much does bed bug treatment cost?", a: "One-bedroom apartments start around $500. Whole-house treatments range $1,200–$2,500 depending on square footage and severity. Free inspection first." },
      { q: "Do I have to throw out my mattress?", a: "Usually no. Mattress encasements + heat + chemical treatment can save it. We only recommend disposal in extreme cases." },
      { q: "How long until they're gone?", a: "Most infestations are eliminated in 2–4 weeks with proper follow-up. Heat treatment can knock down 95%+ in one day." },
    ],
  },
  wildlife: {
    slug: "wildlife",
    code: "S-04",
    title: "Wildlife Removal",
    h1: "Wildlife Removal in Pittsburgh",
    tagline: "Humane. Licensed. Effective.",
    metaTitle: "Wildlife Removal Pittsburgh · Raccoon, Squirrel, Bat, Groundhog · Blue Collar World",
    metaDescription: "Licensed nuisance wildlife removal in Pittsburgh. Humane trapping, exclusion, and repair for raccoons, squirrels, bats, groundhogs. Call 484-764-3392.",
    hero:
      "Pittsburgh's tree canopy and older homes are heaven for nuisance wildlife. We trap humanely, seal entry points, and repair the damage. PA-licensed for nuisance wildlife control across Allegheny County.",
    signs: [
      "Scratching, thumping, or scampering in attic or walls (especially at dawn/dusk)",
      "Chewed soffits, roof lines, or vent screens",
      "Torn-up lawn from grubbing raccoons or skunks",
      "Groundhog burrows near foundation, deck, or garden",
      "Bat guano piles in attic or garage",
      "Squirrel nests visible in eaves or attic vents",
    ],
    process: [
      { title: "Property Assessment", body: "Find every entry point, active nesting site, and damage location. Photo documentation." },
      { title: "Humane Trapping / Eviction", body: "One-way exclusion doors for bats and squirrels. Live-trap and relocate for raccoons and groundhogs per PA regulations." },
      { title: "Exclusion & Sealing", body: "Every entry point sealed with heavy-gauge steel mesh, hydraulic cement, or matched trim." },
      { title: "Sanitation & Repair", body: "Attic remediation (guano, urine, contaminated insulation), soffit repair, screen replacement." },
    ],
    pests: ["Raccoons", "Squirrels (gray, fox, flying)", "Bats", "Groundhogs / Woodchucks", "Opossums", "Skunks", "Chipmunks"],
    faqs: [
      { q: "Do you kill the animals?", a: "No. We use humane trapping and exclusion methods per PA Game Commission regulations. Bats are excluded (never harmed) using one-way doors." },
      { q: "How much does wildlife removal cost?", a: "Ranges from $250 (single squirrel) to $2,500+ (full bat colony with attic remediation). Free inspection and written quote." },
      { q: "Do you fix the damage they caused?", a: "Yes. Soffit repair, vent replacement, insulation replacement, and full attic sanitation are all part of our wildlife service." },
    ],
  },
  mosquito: {
    slug: "mosquito",
    code: "S-05",
    title: "Mosquito Control",
    h1: "Mosquito Control in Pittsburgh",
    tagline: "Reclaim your yard.",
    metaTitle: "Mosquito Control Pittsburgh · Yard Spraying · Blue Collar World Pest Control",
    metaDescription: "Season-long mosquito control in Pittsburgh. Barrier sprays every 21 days. Kid & pet safe. West Nile & EEE protection. Call 484-764-3392 for a free quote.",
    hero:
      "Pittsburgh summers = mosquitoes + humidity. West Nile Virus and Eastern Equine Encephalitis are both reported in Allegheny County. Our barrier program keeps your yard usable May–October.",
    signs: [
      "Mosquitoes swarming at dusk or dawn in your yard",
      "Standing water in gutters, planters, kiddie pools, birdbaths",
      "Getting bit while grilling, mowing, or gardening",
      "Kids or pets covered in bites",
      "Larvae wriggling in stagnant water sources",
    ],
    process: [
      { title: "Property Survey", body: "Identify breeding sites — standing water, dense vegetation, drainage issues." },
      { title: "Larvicide Application", body: "BTI larvicide treats standing water sources so mosquitoes can't complete their life cycle." },
      { title: "Barrier Spray", body: "Backpack-applied residual on foliage, fence lines, and yard perimeter every 21 days." },
      { title: "Season Coverage", body: "May through October program. Special event sprays available for weddings, cookouts, graduations." },
    ],
    pests: ["Asian tiger mosquitoes", "Common house mosquitoes", "Culex (West Nile vector)", "Aedes species"],
    faqs: [
      { q: "How much does mosquito treatment cost?", a: "Typical Pittsburgh yard: $80–$120 per treatment, every 3 weeks. Season packages available at discounted rates." },
      { q: "Is the spray safe for kids and pets?", a: "Yes. Family and pet safe once dry (about 30 minutes). We use pyrethroid formulations approved by the EPA for residential yards." },
      { q: "Does it kill bees?", a: "We time applications away from flowering plants and pollinator hours to protect beneficial insects. Ask about our pollinator-safe program." },
    ],
  },
  households: {
    slug: "households",
    code: "S-01",
    title: "Household Pest Control",
    h1: "Household Pest Control in Pittsburgh",
    tagline: "One team. Every pest.",
    metaTitle: "Household Pest Control Pittsburgh · Roaches, Ants, Rodents · Blue Collar World",
    metaDescription: "Comprehensive household pest control in Pittsburgh. Roaches, ants, spiders, rodents, silverfish. Quarterly plans + one-time treatments. Call 484-764-3392.",
    hero:
      "Whether it's roaches in a Squirrel Hill apartment or field mice invading a Mt. Lebanon Cape Cod, we treat every common Pittsburgh household pest with proven, family-safe methods.",
    signs: [
      "Droppings in cabinets, drawers, or along baseboards",
      "Scratching or scurrying inside walls",
      "Ant trails on countertops, especially spring/fall",
      "Roach sightings — even one means dozens more hidden",
      "Chewed food packaging or greasy rub marks along walls",
      "Cobwebs in corners, basements, and window frames",
    ],
    process: [
      { title: "Interior Inspection", body: "Kitchens, bathrooms, basements, attics, and utility rooms." },
      { title: "Exterior Perimeter", body: "Foundation, entry points, eaves, garage — where pests come in." },
      { title: "Targeted Treatment", body: "Gel baits, IGRs, dust in voids, exterior granular perimeter, exclusion caulking." },
      { title: "Ongoing Protection", body: "Quarterly plan hits pests through every season with free re-treatments between visits." },
    ],
    pests: ["German + American roaches", "Odorous house ants", "Carpenter ants", "House mice + Norway rats", "Silverfish", "Spiders (common house, cellar)", "Centipedes", "Earwigs"],
    faqs: [
      { q: "Do I need to leave during treatment?", a: "For standard interior treatments, no. Just keep kids and pets out of treated rooms for 2 hours until dry." },
      { q: "Which plan is right for me?", a: "Quarterly is best for prevention and long-term protection. One-Time is best for active infestations you want gone fast." },
      { q: "How soon will I see results?", a: "Roaches and ants: 3–7 days for major population decline. Rodents: 10–14 days for full elimination with bait stations." },
    ],
  },
  "parks-schools": {
    slug: "parks-schools",
    code: "S-02",
    title: "Parks & Schools Pest Management",
    h1: "Pest Control for Parks & Schools in Pittsburgh",
    tagline: "IPM-certified. Child-safe. Fully documented.",
    metaTitle: "School Pest Control Pittsburgh · IPM Certified · Blue Collar World",
    metaDescription: "Licensed pest management for Pittsburgh public schools, parks, playgrounds and childcare facilities. IPM protocols, child-safe methods, full documentation. Call 484-764-3392.",
    hero:
      "Schools and parks require IPM (Integrated Pest Management) protocols with the strictest safety standards. We provide licensed, documented, child-safe pest programs for districts, playgrounds, and youth facilities across Allegheny County.",
    signs: [
      "Recurring wasp or hornet activity near playground equipment",
      "Rodent evidence in cafeterias, dumpster areas, storage rooms",
      "Ant infestations in classroom or admin buildings",
      "Tick or flea complaints from athletic fields",
      "Mosquito complaints during outdoor events",
      "Bird nesting on structures creating sanitation issues",
    ],
    process: [
      { title: "Site Assessment + IPM Plan", body: "Written Integrated Pest Management plan tailored to facility use. Prioritizes non-chemical solutions." },
      { title: "Non-Chemical First", body: "Sanitation recommendations, exclusion work, monitoring stations before any chemical intervention." },
      { title: "Child-Safe Materials Only", body: "When treatment is required, we use lowest-toxicity options approved for schools. No general spraying." },
      { title: "Full Documentation", body: "Complete service logs, EPA labels, SDS sheets, and 48-hour parent notification support per PA Act 36." },
    ],
    pests: ["Wasps + hornets", "Rodents", "Ants (multiple species)", "Ticks", "Mosquitoes", "Cockroaches", "Bed bugs (dorm/rec centers)", "Nuisance birds"],
    faqs: [
      { q: "Are you compliant with PA Act 36 (school pesticide law)?", a: "Yes. Full compliance with the PA Public School Integrated Pest Management Act. We handle parent notifications, service logs, and material documentation." },
      { q: "Do you service private schools and daycares?", a: "Yes. Private schools, preschools, daycares, and youth camps all get the same IPM-first protocols." },
      { q: "How quickly can you respond to urgent issues?", a: "24/7 emergency line for active safety issues (wasp nests near entrances, rodent sightings in food areas). Same-day for most requests." },
    ],
  },
};

export const servicesList = Object.values(services);
